/* eslint-disable react/require-default-props, react/forbid-prop-types, max-len */
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Bell, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import NotificationItem from './NotificationItem';

function NotificationPopup({ isOpen, onClose, notifications, onStatusChange }) {
  const popupRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Close on escape key
  useEffect(() => {
    function handleEscape(event) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const activeNotifications = notifications.filter(
    (n) => n.status === 'PENDING' || n.status === 'OPENED',
  );

  return (
    <div
      ref={popupRef}
      className="absolute right-0 top-full mt-2 w-96 max-w-[calc(100vw-2rem)] rounded-lg border bg-popover shadow-lg animate-in fade-in-0 zoom-in-95 z-50"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-primary" />
          <h3 className="font-semibold text-foreground">Notifications</h3>
          {activeNotifications.length > 0 && (
            <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-xs font-medium text-primary-foreground">
              {activeNotifications.length}
            </span>
          )}
        </div>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Content */}
      <ScrollArea className="max-h-96">
        {activeNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <div className="rounded-full bg-muted p-3 mb-3">
              <Bell className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground text-center">No new notifications</p>
          </div>
        ) : (
          <div className="divide-y">
            {activeNotifications.map((notification) => (
              <NotificationItem
                key={notification._id}
                notification={notification}
                onStatusChange={onStatusChange}
              />
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

NotificationPopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  notifications: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      userId: PropTypes.string.isRequired,
      details: PropTypes.object.isRequired,
      createdAt: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onStatusChange: PropTypes.func.isRequired,
};

export default NotificationPopup;
